import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
  useCallback,
} from 'react';
import { checkApiHealth } from 'app/helpers/ApiHealthCheck';

const MaintenanceContext = createContext();

export const useMaintenance = () => {
  const context = useContext(MaintenanceContext);
  if (!context) {
    throw new Error('useMaintenance must be used within MaintenanceProvider');
  }
  return context;
};

export const MaintenanceProvider = ({ children }) => {
  const [isMaintenanceMode, setIsMaintenanceMode] = useState(false);
  const [isCheckingHealth, setIsCheckingHealth] = useState(false);
  const intervalRef = useRef(null);

  const performHealthCheck = useCallback(async (timeout = 5000) => {
    setIsCheckingHealth(true);
    try {
      const isHealthy = await checkApiHealth(timeout);
      setIsMaintenanceMode(!isHealthy);
      return isHealthy;
    } catch (error) {
      setIsMaintenanceMode(true);
      return false;
    } finally {
      setIsCheckingHealth(false);
    }
  }, []);

  // Start periodic health checks when in maintenance mode
  useEffect(() => {
    if (isMaintenanceMode) {
      // Check every 10 seconds
      intervalRef.current = setInterval(async () => {
        const isHealthy = await performHealthCheck();
        if (isHealthy) {
          // If health check passes, redirect to PUBLIC_URL
          const publicUrl = process.env.PUBLIC_URL || window.location.origin;
          window.location.href = publicUrl;
        }
      }, 10000);
    } else {
      // Clear interval when not in maintenance mode
      // eslint-disable-next-line no-lonely-if
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [isMaintenanceMode, performHealthCheck]);

  const value = {
    isMaintenanceMode,
    isCheckingHealth,
    performHealthCheck,
    setIsMaintenanceMode,
  };

  return (
    <MaintenanceContext.Provider value={value}>
      {children}
    </MaintenanceContext.Provider>
  );
};

